/**
 * Cadence policy for the live-status poll.
 *
 * The poll does two jobs, and only one of them is change detection. While
 * hls.js is sitting on a 404'd live playlist it has stopped loading, and the
 * poll is what retries the source - so in that state the cadence gates
 * playback outright. The configured 5s interval is a reasonable rate to
 * notice a status change at; it is a poor rate to retry first bytes at.
 *
 * So: poll fast only while a retry is outstanding, and use the configured
 * interval everywhere else. Deliberately not a general "poll faster whenever
 * the stream is not playable" - every poll reaches a database read on the
 * origin, and a pre-start audience polling in lockstep is the cost the 5s
 * interval exists to avoid.
 */

/** Fast cadence, used while a playlist retry is outstanding. */
export const WAITING_POLL_MIN_MS = 1000;

/**
 * How many consecutive fast retries a public embed gets before falling back
 * to the configured interval.
 *
 * A playlist 404 normally clears within a few seconds of the encoder
 * producing its first segments, but it does not have to: a stream whose
 * encoder dies stays 404 indefinitely, and without a bound its whole audience
 * would retry once a second forever - during exactly the incident where the
 * origin can least afford it. 15 covers the normal wait several times over.
 */
export const MAX_FAST_RETRY_POLLS = 15;

/**
 * Spread applied on top of the fast cadence, as a fraction of it. Waiting
 * clients synchronize: a public audience all become playable on the same
 * Start, and all 404 together if the first segments lag. Without a spread
 * their retries converge into a single tick and arrive as one burst, against
 * an origin that is simultaneously bringing the stream up.
 *
 * Applied upward only, so WAITING_POLL_MIN_MS stays a hard floor.
 */
export const POLL_JITTER_RATIO = 0.2;

export interface ResolveLiveStatusPollDelayOptions {
  /** The configured `live-status-interval`, in ms. Also the slowest cadence. */
  baseIntervalMs: number;
  /**
   * Whether a live playlist load is outstanding - hls.js 404'd on it and is
   * waiting for this poll to retry the source.
   *
   * This, not `LiveStreamStatus.Preview`, is what "the broadcaster is
   * publishing but there is nothing to play yet" looks like from the player.
   * A preview player is playable from the moment it loads, and nothing in the
   * live-status response changes when publishing starts, so the outstanding
   * retry is the only signal there is.
   */
  isAwaitingPlaylist: boolean;
  /** Consecutive polls made with a retry outstanding. */
  outstandingRetryPolls: number;
  /**
   * Whether this player was opened as a preview (`livepreview=true`), which
   * exempts it from the retry bound. The bound protects the origin from an
   * audience; a preview player is the single operator who opened it, so its
   * cost never scales and it stays responsive to a broadcaster who starts
   * publishing long after opening the tab.
   */
  isPreviewPlayer?: boolean;
  /** Injectable for tests; expected to return [0, 1). */
  random?: () => number;
}

/**
 * Delay before the next live-status poll, always in
 * [min(baseIntervalMs, WAITING_POLL_MIN_MS), baseIntervalMs] - so this can
 * only ever poll more often than configured, never less.
 */
export function resolveLiveStatusPollDelay({
  baseIntervalMs,
  isAwaitingPlaylist,
  outstandingRetryPolls,
  isPreviewPlayer = false,
  random = Math.random,
}: ResolveLiveStatusPollDelayOptions): number {
  const base = Number.isFinite(baseIntervalMs) && baseIntervalMs > 0
    ? baseIntervalMs
    : WAITING_POLL_MIN_MS;

  if (!isAwaitingPlaylist) return base;
  if (!isPreviewPlayer && outstandingRetryPolls >= MAX_FAST_RETRY_POLLS) {
    return base;
  }

  const floor = Math.min(base, WAITING_POLL_MIN_MS);

  return Math.min(
    base,
    Math.round(floor * (1 + POLL_JITTER_RATIO * random())),
  );
}
